import {
  validateCreditCartPricingQuote,
  type PricingDisplayContract,
  type PricingDisplayQuote,
} from '@/lib/billing/pricingDisplay';
import type { PaymentIntentKeyInput } from '@/lib/paymentSafety';

export const CREDIT_PRICING_BINDING_VERSION = 'credit_pricing_binding.v1';
export const PRICING_DISPLAY_CONTRACT_VERSION = 'customer_pricing_display.v1';

export interface CreditPricingBindingRequest {
  pricing_contract_version: typeof PRICING_DISPLAY_CONTRACT_VERSION;
  binding_version: typeof CREDIT_PRICING_BINDING_VERSION;
  tax_config_id: string;
  items: Array<{
    credit_pack_product_id: string;
    quantity: number;
    unit_pricing: PricingDisplayContract;
    line_pricing: PricingDisplayContract;
  }>;
  cart_total_pricing: PricingDisplayContract;
  displayed_gross_amount_halalas: number;
  quote_fingerprint: string;
}

function creditCartIdentity(
  items: Array<{ packId: string; quantity: number }>,
): string {
  return items
    .map((item) => `${item.packId}x${item.quantity}`)
    .sort()
    .join('+');
}

function creditBindingCartIdentity(
  items: CreditPricingBindingRequest['items'],
): string {
  return items
    .map((item) => `${item.credit_pack_product_id}x${item.quantity}`)
    .sort()
    .join('+');
}

export function buildQuoteBoundCreditPaymentAttempt(input: {
  ownerType: 'organization' | 'candidate';
  ownerId: string;
  items: Array<{ packId: string; quantity: number }>;
  pricingBinding: CreditPricingBindingRequest;
}): PaymentIntentKeyInput {
  const cartIdentity = creditCartIdentity(input.items);
  const quoteFingerprint = input.pricingBinding.quote_fingerprint;
  if (
    !input.ownerId ||
    !cartIdentity ||
    cartIdentity !== creditBindingCartIdentity(input.pricingBinding.items) ||
    !/^[a-f0-9]{64}$/.test(quoteFingerprint) ||
    input.pricingBinding.binding_version !== CREDIT_PRICING_BINDING_VERSION ||
    input.pricingBinding.pricing_contract_version !==
      PRICING_DISPLAY_CONTRACT_VERSION
  ) {
    throw new Error('credit_pricing_binding_required');
  }

  return {
    ownerType: input.ownerType,
    ownerId: input.ownerId,
    paymentType: 'credit_pack',
    productKey: `${cartIdentity}:${quoteFingerprint}`,
  };
}

export function buildCreditPricingBinding(
  items: Array<{ packId: string; quantity: number }>,
  quote: PricingDisplayQuote | null | undefined,
): CreditPricingBindingRequest | null {
  const validated = validateCreditCartPricingQuote(items, quote);
  if (
    !validated.ready ||
    !validated.quote ||
    validated.quote.pricing_contract_version !==
      PRICING_DISPLAY_CONTRACT_VERSION ||
    !validated.quote.tax_config_id ||
    !validated.quote.quote_fingerprint
  ) {
    return null;
  }
  const lines = validated.quote.lines ?? [];
  return {
    pricing_contract_version: PRICING_DISPLAY_CONTRACT_VERSION,
    binding_version: CREDIT_PRICING_BINDING_VERSION,
    tax_config_id: validated.quote.tax_config_id,
    items: items.map((item) => {
      const line = lines.find((candidate) => candidate.source_id === item.packId)!;
      return {
        credit_pack_product_id: item.packId,
        quantity: item.quantity,
        unit_pricing: line.unit_display,
        line_pricing: line.display,
      };
    }),
    cart_total_pricing: validated.quote.display,
    displayed_gross_amount_halalas:
      validated.quote.display.gross_amount_halalas,
    quote_fingerprint: validated.quote.quote_fingerprint,
  };
}
